import React, { useCallback, useEffect, useState } from 'react';

import { TourProvider, useNavigate, useSelector, useTour } from 'third-party';

import {
  bankListUrl,
  memberListUrl,
  tenantListUrl,
  payeeListUrl,
  paymentListUrl,
} from 'constants/url';

import { useUser } from 'modules/user';

import { appSelectors } from 'selectors';

import { APIUser, APIUserSettingModel, APIUserSettingType } from 'types/api';

import { tourStyles } from 'styles/theme';

type StepData = {
  selector: string;
  link?: (user: APIUser) => string;
  content: string;
};

export const steps: StepData[] = [
  {
    selector: '#menu-tenant',
    link: () => tenantListUrl(),
    content:
      'Welcome to your tenant. Here you can manage your tenant settings and preferences.',
  },
  {
    selector: '#menu-tenant-bank',
    link: (user: APIUser) => bankListUrl(user.userTenants[0].tenant.id),
    content:
      'This is where you create and manage banks. Personalize your check layouts - add logo, QR codes, signatures, and adjust formatting.',
  },
  {
    selector: '#menu-tenant-payment',
    link: (user: APIUser) => paymentListUrl(user.userTenants[0].tenant.id),
    content: 'This is where you create and track payments',
  },
  {
    selector: '#menu-tenant-payee',
    link: (user: APIUser) => payeeListUrl(user.userTenants[0].tenant.id),
    content:
      'Here you manage all payees in your tenant. You can create new payees, edit or delete payees',
  },
  {
    selector: '#menu-tenant-member',
    link: (user: APIUser) => memberListUrl(user.userTenants[0].tenant.id),
    content:
      'Here you manage all users in your tenant. You can create new users, edit or delete users',
  },

  {
    selector: '#user-avatar',
    content:
      'This is your user avatar. Click here to access your account settings.',
  },
  {
    selector: '#info-links',
    link: (user: APIUser) => memberListUrl(user.userTenants[0].tenant.id),
    content: 'Need more information? Check our help materials here.',
  },
];

export const useCheckTour = () => {
  const { isOpen, currentStep, steps, setIsOpen, setCurrentStep, setSteps } =
    useTour();
  const navigate = useNavigate();
  const user = useSelector(appSelectors.user);
  const userSettings = React.useMemo(
    () => user?.settings ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id],
  );
  const { loadUserSettings, updateUserSettings } = useUser();

  const isTourCompleted = userSettings?.some(
    (setting: APIUserSettingModel) =>
      setting.type === APIUserSettingType.UserTour &&
      setting.value === 'completed',
  );

  useEffect(() => {
    if (!userSettings && user) {
      loadUserSettings();
    }
  }, [userSettings, user, loadUserSettings]);

  useEffect(() => {
    if (user && userSettings && !isTourCompleted) {
      navigate(tenantListUrl());
      setIsOpen(true);
      completeTour();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, userSettings, isTourCompleted, setIsOpen, navigate]);

  const completeTour = useCallback(() => {
    updateUserSettings({
      type: APIUserSettingType.UserTour,
      value: 'completed',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateUserSettings, setIsOpen]);

  return {
    isOpen,
    currentStep,
    steps,
    setIsOpen,
    setCurrentStep,
    setSteps,
    completeTour,
    isTourCompleted,
  };
};

type Props = {
  children: React.ReactNode;
};

export const TourContainer: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const user = useSelector(appSelectors.user);

  const setCurrentStep = (step: number | ((prevStep: number) => number)) => {
    const stepNumber = typeof step === 'function' ? step(0) : step;
    const currentStep = steps[stepNumber];
    if (currentStep && currentStep.link && user) {
      navigate(currentStep.link(user));
    }
    setTimeout(() => {
      setStep(typeof step === 'function' ? step(0) : step);
    }, 100);
  };

  return (
    <TourProvider
      steps={steps}
      currentStep={step}
      setCurrentStep={setCurrentStep}
      styles={tourStyles}
    >
      {children}
    </TourProvider>
  );
};
